import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  LinearProgress,
  Paper,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Fab,
  Checkbox
} from '@mui/material';
import { UploadFile, Description, Delete, CheckCircle, Close, Download, Add, DeleteSweep, CloudDownload } from '@mui/icons-material';

// Render as a standalone page/component. Props:
// - postID: the id to attach the upload to
// - onImportSuccess(filesCount): optional callback when import starts/completes
const NormalizedResume = ({ postID = null, onImportSuccess = null }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [normalizingIds, setNormalizingIds] = useState(new Set());
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10); // For pagination
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, resumeId: null }); // For delete confirmation
  const [processingTimeLeft, setProcessingTimeLeft] = useState(0); // For processing time display
  const [isProcessing, setIsProcessing] = useState(false); // Track if processing is ongoing
  const [selectedItems, setSelectedItems] = useState([]); // For multi-select
  const [multiDeleteConfirmation, setMultiDeleteConfirmation] = useState(false); // For multi-delete confirmation
  const apiUrl = process.env.REACT_APP_API_URL || '';



  // Constants based on backend restrictions
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_FILES = 1001;
  const ALLOWED_EXTENSIONS = ['pdf']; // Backend only accepts PDF

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    addFiles(newFiles);
  };
  function getUserIdFromAccessToken() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      const userId = decodedToken.user_id;
      return userId;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  }

  function parseJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  }

  const userId = getUserIdFromAccessToken();

  const validateFile = (file) => {
    // Check file extension
    const extension = getFileExtension(file.name).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      return t('importResumes.notPdfFile', { fileName: file.name });
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return t('importResumes.fileTooLarge', { fileName: file.name });
    }

    return null; // No errors
  };

  const simulateFileUpload = (file) => {
    setUploading(true);
    setUploadProgress(prev => ({
      ...prev,
      [file.name]: 0
    }));

    const interval = setInterval(() => {
      setUploadProgress(prev => {
        const currentProgress = prev[file.name] || 0;
        const newProgress = currentProgress + 10;

        if (newProgress >= 100) {
          clearInterval(interval);
          setUploading(false);
          return {
            ...prev,
            [file.name]: 100
          };
        }

        return {
          ...prev,
          [file.name]: newProgress
        };
      });
    }, 300);
  };

  const removeFile = (fileName) => {
    setFiles(prev => prev.filter(file => file.name !== fileName));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  // Unified function to handle both input and drop files
  const addFiles = (newFiles) => {
    if (files.length + newFiles.length > MAX_FILES) {
      setUploadError(t('importResumes.maxFilesExceeded', { max: MAX_FILES }));
      return;
    }

    const validFiles = [];
    const errors = [];

    newFiles.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setUploadError(t('importResumes.someFilesNotAdded', { errors: errors.join(', ') }));
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
      validFiles.forEach(file => simulateFileUpload(file));
    }
  };

  const getFileExtension = (filename) => {
    return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2);
  };

  const isResume = (filename) => {
    const ext = getFileExtension(filename).toLowerCase();
    return ALLOWED_EXTENSIONS.includes(ext);
  };

  async function Load_applications() {
    try {
      setLoadingApplications(true);
      const response = await fetch(
        `${apiUrl}candidates/list_normalised_resume/${userId}/`,
        {
          headers: {
            "Content-type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        }
      );
      if (response.status === 401) {
        return;
      }
      const json = await response.json();
      console.log("NORMALISED RESUMES data:", json);

      // FIX: Access the nested array
      setApplications(json["resumes norm"] || []);

    } catch (error) {
      console.error("Error loading normalised resumes:", error);
    } finally {
      setLoadingApplications(false);
    }
  }

  // Load applications when component mounts
  useEffect(() => {
    Load_applications();
  }, []);

  // Effect to handle processing time countdown
  useEffect(() => {
    let interval;
    if (isProcessing && processingTimeLeft > 0) {
      interval = setInterval(() => {
        setProcessingTimeLeft(prev => {
          const newTime = prev - 1;
          if (newTime <= 0) {
            // Call Load_applications when processing is complete
            Load_applications();
            setIsProcessing(false);
            return 0;
          }
          return newTime;
        });
      }, 1000);
    } else if (processingTimeLeft === 0 && isProcessing) {
      setIsProcessing(false);
    }
    return () => clearInterval(interval);
  }, [isProcessing, processingTimeLeft]);

  const uploadFiles = async () => {
    if (files.length === 0) return;
    // Close the dialog immediately when import starts
    setOpen(false);

    // Calculate processing time: number of files * 20 seconds
    const totalProcessingTime = files.length * 20;
    setProcessingTimeLeft(totalProcessingTime);
    setIsProcessing(true);

    // Notify parent that import is starting (if provided)
    if (onImportSuccess) {
      try {
        onImportSuccess(files.length); // Pass the number of files
      } catch (e) {
        // ignore callback errors
      }
    }

    setUploading(true);
    setUploadError(null);
    setUploadResult(null);

    try {
      const formData = new FormData();

      // Append each file to the FormData object
      files.forEach((file) => {
        formData.append("files[]", file);
      });
      console.log("Files to upload:", files);
      console.log("FormData:", formData);

      // Make the API call
      const response = await fetch(
        `${apiUrl}candidates/create_normalised_resume/${userId}/`,  //ai
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
          body: formData,
        }
      );

      // Parse response data
      const data = await response.json();

      // Check if response is successful
      if (!response.ok) {
        throw new Error(data.error || `Upload failed with status: ${response.status}`);
      }

      console.log("Upload response:", data);

      // Set upload result
      setUploadResult(data);

      // Show success alert
      setShowSuccessAlert(true);

      // Reload applications after successful upload
      Load_applications();
    } catch (error) {
      console.error("Error uploading files:", error);
      setUploadError(error.message || "Failed to upload files");
      setIsProcessing(false);
      setProcessingTimeLeft(0);
    } finally {
      setUploading(false);
      // Clean up state after upload
      setFiles([]);
      setUploadProgress({});
    }
  };


  const importButtonSx = {
    px: 3,
    py: 1,
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: '0 6px 18px rgba(2,6,23,0.08)',
    background: 'linear-gradient(90deg,#0066FF 0%, #0052CC 100%)',
    color: '#fff',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 10px 28px rgba(2,6,23,0.12)'
    }
  };

  // Function to download a file
  const downloadFile = async (file, filename) => {
    let resume_path;

    // Handle different file input types
    if (typeof file === "string") {
      resume_path = file;
    } else if (file && typeof file === "object") {
      resume_path = file.resume_path || file.path || file.name;
    } else {
      console.error("Invalid file type", file);
      return;
    }

    if (!resume_path) {
      console.log("Resume path is not available");
      return;
    }

    // Ensure the resume_path uses forward slashes
    const normalizedResumePath = resume_path.replace(/\\/g, "/");

    // Remove leading slash or /media/ if present to avoid double slashes
    const cleanPath = normalizedResumePath.replace(/^\/media\//, '').replace(/^\//, '');
    const fileURL = `${apiUrl}media/${cleanPath}`;

    try {
      const response = await fetch(fileURL, {
        method: "GET",
        headers: {
          Authorization: "Bearer " + localStorage.getItem("accessToken"),
        },
      });

      if (response.ok) {
        // Create a blob from the response
        const blob = await response.blob();

        // Create a temporary URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create a temporary anchor element and trigger download
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || extractFilename(resume_path);
        document.body.appendChild(a);
        a.click();

        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        console.log(`File downloaded: ${filename}`);
      } else {
        console.error(`Failed to download file. Status: ${response.status}`);
        alert("Failed to download file. Please try again.");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Error downloading file. Please check your connection.");
    }
  };

  // Helper function to extract filename from path
  const extractFilename = (filePath) => {
    if (!filePath) return 'N/A';
    // Split by both forward and backward slashes and get the last part
    const parts = filePath.split(/[\/\\]/);
    return parts[parts.length - 1] || 'N/A';
  };

  // Helper function to generate normalized filename
  const generateNormalizedFilename = (originalPath) => {
    if (!originalPath) return 'N/A';
    const filename = extractFilename(originalPath);
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return `${filename}_normalised`;
    }
    const name = filename.substring(0, lastDotIndex);
    const extension = filename.substring(lastDotIndex);
    return `${name}_normalised${extension}`;
  };

  // Function to delete a resume
  const handleShowFile = async (key, file) => {
    // Toggle expanded row

    let fileURL;
    let resume_path;

    if (typeof file === "string") {
      resume_path = file;
    } else if (file && typeof file === "object") {
      resume_path = file.resume_path || file.path || file.name;
    } else {
      console.error("Invalid file type", file);
      return;
    }

    if (!resume_path) {
      console.log("Resume path is not available");
      return;
    }

    // Ensure the resume_path uses forward slashes
    const normalizedResumePath = resume_path.replace(/\\/g, "/");
    fileURL = `${apiUrl}media/${normalizedResumePath}`;

    try {
      const response = await fetch(`${apiUrl}ai/search_resume/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ resume_url: fileURL }),
      });

      const contentType = response.headers.get("content-type");

      if (response.ok) {
        if (contentType && contentType.includes("application/pdf")) {
          // Create a blob from the PDF content
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          // Open the PDF in a new tab
          window.open(url, "_blank");
        } else {
          console.log("Unexpected response from server");
        }
      } else {
        if (contentType && contentType.includes("text/html")) {
          // Handle HTML response (404 page)
          const htmlContent = await response.text();
          // Create a new window and write the HTML content to it
          const newWindow = window.open();
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        } else {
          const data = await response.json();
          console.log(data.message);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // Function to open delete confirmation dialog
  const confirmDelete = (resumeId) => {
    setDeleteConfirmation({ open: true, resumeId });
  };

  // Function to close delete confirmation dialog
  const closeDeleteConfirmation = () => {
    setDeleteConfirmation({ open: false, resumeId: null });
  };

  // Function to delete a resume after confirmation
  const deleteResume = async (resumeId) => {
    try {
      const response = await fetch(
        `${apiUrl}candidates/delete_normalised_resume/${resumeId}/`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('accessToken'),
          },
        }
      );

      if (response.ok) {
        // Reload applications after successful deletion
        Load_applications();
      } else {
        console.error('Failed to delete resume');
      }
    } catch (error) {
      console.error('Error deleting resume:', error);
    } finally {
      closeDeleteConfirmation();
    }
  };

  // Function to show more resumes
  const showMore = () => {
    setVisibleCount(prevCount => prevCount + 10);
  };

  // Multi-select handlers
  const handleSelectItem = (id) => {
    setSelectedItems(prev => {
      if (prev.includes(id)) {
        return prev.filter(itemId => itemId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    const visibleApps = applications.slice(0, visibleCount);
    if (selectedItems.length === visibleApps.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(visibleApps.map(app => app.id));
    }
  };

  const isItemSelected = (id) => selectedItems.includes(id);

  // Multi-delete handler
  const handleMultiDelete = async () => {
    try {
      for (const resumeId of selectedItems) {
        await fetch(
          `${apiUrl}candidates/delete_normalised_resume/${resumeId}/`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + localStorage.getItem('accessToken'),
            },
          }
        );
      }
      // Reload applications after successful deletion
      Load_applications();
      setSelectedItems([]);
    } catch (error) {
      console.error('Error deleting resumes:', error);
    } finally {
      setMultiDeleteConfirmation(false);
    }
  };

  // Multi-download handler
  const handleMultiDownload = async () => {
    const selectedApps = applications.filter(app => selectedItems.includes(app.id));
    for (const app of selectedApps) {
      if (app.normalised_path) {
        await downloadFile(app.normalised_path, extractFilename(app.normalised_path));
        // Add a small delay between downloads to prevent browser issues
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '1200px', mx: 'auto', px: { xs: 2, md: 0 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mtd: 3, ml: { xs: 0, md: 2 }, mr: { xs: 0, md: 2 } }}>
        <Button startIcon={<UploadFile />} sx={importButtonSx} onClick={() => setOpen(true)}>
          {t('importResumes.title') || 'Import CV'}
        </Button>

        {/* Processing Time Display */}
        {isProcessing && processingTimeLeft > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 0.5,
              minWidth: '200px'
            }}
          >
            <Typography
              sx={{
                color: '#1976d2',
                fontWeight: 600,
                fontSize: '14px',
                fontFamily: 'Poppins'
              }}
            >
              ⏱️ Durée de traitement: {processingTimeLeft}s
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(1 - processingTimeLeft / (files.length * 20)) * 100}
              sx={{
                width: '100%',
                height: 6,
                borderRadius: '2px',
                backgroundColor: 'rgba(25, 118, 210, 0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#1976d2'
                }
              }}
            />
          </Box>
        )}
      </Box>

      {/* Display list of resumes */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" textTransform='none'>
            {t('importResumes.resumeList') || 'Resume List'}
          </Typography>

          {/* Multi-action buttons */}
          {selectedItems.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteSweep />}
                onClick={() => setMultiDeleteConfirmation(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                }}
              >
                {t('importResumes.multiDelete') || 'Multi Delete'} ({selectedItems.length})
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudDownload />}
                onClick={handleMultiDownload}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                  background: 'linear-gradient(90deg, #0066FF 0%, #0052CC 100%)',
                }}
              >
                {t('importResumes.multiDownload') || 'Multi Download'} ({selectedItems.length})
              </Button>
            </Box>
          )}
        </Box>

        {loadingApplications ? (
          <Typography>{t('importResumes.loading') || 'Loading...'}</Typography>
        ) : applications && applications.length > 0 ? (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedItems.length > 0 && selectedItems.length < applications.slice(0, visibleCount).length}
                        checked={applications.slice(0, visibleCount).length > 0 && selectedItems.length === applications.slice(0, visibleCount).length}
                        onChange={handleSelectAll}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>{t('importResumes.id') || 'ID'}</TableCell>
                    <TableCell>{t('importResumes.originalResume') || 'Original Resume'}</TableCell>
                    <TableCell>{t('importResumes.normalizedPath') || 'Normalized Path'}</TableCell>
                    <TableCell>{t('importResumes.date') || 'Date'}</TableCell>
                    <TableCell>{t('importResumes.actions') || 'Actions'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applications.slice(0, visibleCount).map((app, index) => (
                    <TableRow
                      key={app.id || index}
                      selected={isItemSelected(app.id)}
                      sx={{ '&.Mui-selected': { backgroundColor: 'rgba(25, 118, 210, 0.08)' } }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected(app.id)}
                          onChange={() => handleSelectItem(app.id)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>{index + 1}</TableCell>

                      <TableCell
                        onClick={() => handleShowFile(index, app.original_resume || app)}
                        style={{
                          cursor: 'pointer',
                          color: '#1976d2',
                          textDecoration: 'underline',
                          transition: 'color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#1565c0'}
                        onMouseLeave={(e) => e.target.style.color = '#1976d2'}
                      >
                        {extractFilename(app.original_resume)}
                      </TableCell>

                      <TableCell
                        onClick={() => handleShowFile(index, app.normalised_path || app)}
                        style={{
                          cursor: 'pointer',
                          color: '#1976d2',
                          textDecoration: 'underline',
                          transition: 'color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#1565c0'}
                        onMouseLeave={(e) => e.target.style.color = '#1565c0'}
                      >
                        {generateNormalizedFilename(app.original_resume)}
                      </TableCell>

                      <TableCell>{app.date ? new Date(app.date).toLocaleDateString() : 'N/A'}</TableCell>

                      <TableCell>
                        <IconButton
                          onClick={() => downloadFile(app.normalised_path, extractFilename(app.normalised_path))}
                          disabled={!app.normalised_path}
                          size="small"
                          title="Download Original"
                        >
                          <Download />
                        </IconButton>
                        <IconButton
                          onClick={() => confirmDelete(app.id)}
                          size="small"
                          title="Delete"
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {visibleCount < applications.length && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                <Fab color="primary" aria-label="add" onClick={showMore}>
                  {/* {t('see_more') || 'See More'} */}
                  <Add />
                </Fab>
              </Box>
            )}
          </>
        ) : (
          <Typography>{t('importResumes.noResumes') || 'No resumes found'}</Typography>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmation.open} onClose={closeDeleteConfirmation}>
        <DialogTitle>{t('importResumes.confirmDeleteTitle') || 'Confirm Delete'}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('importResumes.confirmDeleteMessage') || 'Are you sure you want to delete this resume? This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirmation} color="primary">
            {t('importResumes.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={() => deleteResume(deleteConfirmation.resumeId)}
            color="error"
            variant="contained"
          >
            {t('importResumes.delete') || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Multi-Delete Confirmation Dialog */}
      <Dialog open={multiDeleteConfirmation} onClose={() => setMultiDeleteConfirmation(false)}>
        <DialogTitle>{t('importResumes.confirmMultiDeleteTitle') || 'Confirm Multi Delete'}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('importResumes.confirmMultiDeleteMessage', { count: selectedItems.length }) || `Are you sure you want to delete ${selectedItems.length} selected resume(s)? This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMultiDeleteConfirmation(false)} color="primary">
            {t('importResumes.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleMultiDelete}
            color="error"
            variant="contained"
          >
            {t('importResumes.deleteAll') || 'Delete All'} ({selectedItems.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { width: { xs: '95%', sm: 680 }, maxHeight: '80vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontFamily="Poppins" fontWeight={500}>{t('importResumes.title')}</Typography>
          <IconButton onClick={() => setOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent dividers>

          {uploadResult && (
            <Alert
              severity='success'
              sx={{ mb: 2 }}
            >
              {uploadResult.success_count} {t('importResumes.filesUploaded')}
              {uploadResult.error_count > 0 && ` ${uploadResult.error_count} ${t('importResumes.filesHadErrors')}`}
            </Alert>
          )}

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <Paper
              variant="outlined"
              sx={{
                width: '100%',
                padding: { xs: 2, md: 4 },
                textAlign: 'center',
                border: `2px dashed ${isDragging ? 'primary.main' : '#ccc'}`,
                borderRadius: 2,
                cursor: 'pointer',
                backgroundColor: isDragging ? 'rgba(0,0,0,0.02)' : 'transparent',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(0, 0, 0, 0.02)'
                }
              }}
              component="label"
              onDragEnter={handleDragEnter}
              onDragOver={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                hidden
                multiple
                accept=".pdf"
                onChange={handleFileChange}
              />
              <UploadFile sx={{ fontSize: { xs: 28, md: 36 }, color: 'primary.main', mb: 1 }} />
              <Typography variant="subtitle2" fontFamily="Poppins" gutterBottom>
                {t('importResumes.dragDrop')}
              </Typography>
              <Typography variant="body2" color="textSecondary" fontFamily="Poppins">
                {t('importResumes.supportedFormat')}
              </Typography>
            </Paper>
          </Box>

          {files.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontFamily="Poppins" fontWeight={600} sx={{ mb: 1 }}>
                {t('importResumes.uploadedResumes')} ({files.length})
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {files.map((file, index) => (
                  <Paper key={`${file.name}-${index}`} variant="outlined" sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Description color={isResume(file.name) ? 'primary' : 'error'} sx={{ fontSize: 20 }} />
                        <Typography variant="body2" fontFamily="Poppins" fontWeight={500} noWrap sx={{ maxWidth: '220px' }}>{file.name}</Typography>
                      </Box>
                      <IconButton onClick={() => removeFile(file.name)} size="small"><Delete fontSize="small" /></IconButton>
                    </Box>
                    {/* Progress bar for file upload */}
                    {uploadProgress[file.name] < 100 ? (
                      <Box sx={{ width: '100%', mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={uploadProgress[file.name] || 0}
                          sx={{ height: 8, borderRadius: 1 }}
                        />
                        <Typography variant="caption" color="textSecondary" align="right" display="block">
                          {uploadProgress[file.name] || 0}%
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <CheckCircle color="success" fontSize="small" />
                        <Typography variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>{(file.size / 1024).toFixed(1)} KB</Typography>
                      </Box>
                    )}

                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
            {t('importResumes.normaliseNote')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" onClick={() => { setFiles([]); setUploadProgress({}); setUploadError(null); }} disabled={uploading}>{t('importResumes.cancel')}</Button>
          <Button variant="contained" disabled={uploading} onClick={uploadFiles}>{uploading ? t('importResumes.uploading') : `${t('importResumes.normalise')} ${files.length > 0 ? `(${files.length})` : ''}`}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={3000}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSuccessAlert(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {t('importResumes.successfullyUploaded')}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default NormalizedResume;